import "./document.css";
import {
  profilePurpleIcons,
  profileFilledDarkIcons,
} from "../../utils/globalImages";
import {
  TEN_MB_FILE_SIZE,
  FURTHER_VERIFICATION,
} from "../../utils/GlobalConstants";
import { useSelector } from "react-redux";
import { Eye, X } from "lucide-react";
import Toaster from "../../components/Toast/Toast";
import { useState, useRef, useEffect, useCallback } from "react";
import ReadMoreText from "../../components/ReadMore/ReadMoreText";
import { Document, GetDocument } from "../../api/modules/b2droppie.class";
import { formatDateTime, formatLabel } from "../../utils/HelperFunctions";
import BackdropLoader from "../../components/BackdropLoader/BackdropLoader";
import { ReactComponent as Upload } from "../../assets/icons/uploadDoc.svg";

const MyAccount = () => {
  const startYRef = useRef(0);
  const inputRef = useRef(null);
  const currentYRef = useRef(0);
  const dialogRef = useRef(null);
  const errorTimeoutRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 641);
  const user_id = useSelector((state) => state?.auth?.user?.id);
  const [selectedFile, setSelectedFile] = useState(null);
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const verification = useSelector((state) => state.verification);
  const [message, setMessage] = useState({ success: "", error: "" });
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const documentUploadEnabled = useSelector(
    (state) => state?.auth?.user?.document_upload_enabled,
  );
  const verificationStatus = useSelector(
    (state) => state?.auth?.user?.verification_status,
  );
  const furtherVerification = verificationStatus === FURTHER_VERIFICATION;
  const documentVerificationCode = useSelector(
    (state) => state?.auth?.user?.verification_code,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 641);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const statusBadge = (status) => {
    const normalized = status?.toString()?.trim()?.toUpperCase();
    return (
      <span className={`custom-status-badge ${normalized?.toLowerCase()}`}>
        {normalized}
      </span>
    );
  };

  useEffect(() => {
    fetchDocuments();
  }, [selectedFile]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const resp = await GetDocument();
      setDocuments(resp?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile || !selectedDocumentType) {
      setMessage({
        success: "",
        error: "Document type and file are required",
      });
      return;
    }

    const formData = new FormData();
    formData.append("user_document", selectedFile);
    formData.append("document_type", selectedDocumentType);

    try {
      setLoading(true);
      const res = await Document(formData);

      const isSuccess = res?.data?.success;
      const msg =
        res?.data?.message ||
        (isSuccess ? "Document Uploaded Successfully" : "");

      setMessage((prev) => ({
        ...prev,
        success: isSuccess ? msg : "",
        error: !isSuccess ? msg : "",
      }));

      const resp = await GetDocument();

      setDocuments(resp?.data?.data || []);
      setSelectedFile(null);
      handleCloseDialog();
      setSelectedDocumentType("");
    } catch (error) {
      const apiResponse = error?.response?.data;
      let errorMessage = "Something went wrong. Please try again.";
      if (apiResponse?.errors) {
        errorMessage = Object.values(apiResponse.errors).flat().join(" ");
      } else if (apiResponse?.message) {
        errorMessage = apiResponse.message;
      }

      setMessage({
        success: "",
        error: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > TEN_MB_FILE_SIZE) {
        setMessage({
          success: "",
          error: "File size exceeds 10MB. Please upload a smaller file.",
        });
        e.target.value = "";
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSelectFileClick = () => {
    if (!selectedDocumentType) {
      if (errorTimeoutRef.current) return;

      setMessage({
        success: "",
        error: "Please select document type first",
      });

      errorTimeoutRef.current = setTimeout(() => {
        errorTimeoutRef.current = null;
      }, 2000);

      return;
    }

    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }

    inputRef.current.click();
  };

  const handleDocumentTypeChange = (e) => {
    setSelectedDocumentType(e.target.value);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (!selectedDocumentType) {
      setMessage({
        success: "",
        error: "Please select document type first",
      });
      return;
    }

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (file.size > TEN_MB_FILE_SIZE) {
      setMessage({
        success: "",
        error: "File size exceeds 10MB. Please upload a smaller file.",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleView = (doc) => {
    window.open(doc.path, "_blank");
  };

  const handleOpenDialog = () => {
    setShowDialog(true);
    setIsClosing(false);
    setDragOffset(0);
  };

  const handleCloseDialog = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setShowDialog(false);
      setIsClosing(false);
      setSelectedFile(null);
      setSelectedDocumentType("");
      setDragOffset(0);
    }, 300);
  }, []);

  // Drag to close handlers
  const handleTouchStart = useCallback((e) => {
    startYRef.current = e.touches[0].clientY;
    currentYRef.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging) return;

      currentYRef.current = e.touches[0].clientY;
      const diff = currentYRef.current - startYRef.current;

      // Only allow dragging downward
      if (diff > 0) {
        setDragOffset(diff);
        if (dialogRef.current) {
          dialogRef.current.style.transform = `translateY(${diff}px)`;
        }
      }
    },
    [isDragging],
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);

    const diff = currentYRef.current - startYRef.current;

    // If dragged more than 100px down, close the dialog
    if (diff > 100) {
      handleCloseDialog();
    } else {
      // Snap back to original position
      setDragOffset(0);
      if (dialogRef.current) {
        dialogRef.current.style.transform = "translateY(0)";
        dialogRef.current.style.transition = "transform 0.3s ease";
        setTimeout(() => {
          if (dialogRef.current) {
            dialogRef.current.style.transition = "";
          }
        }, 300);
      }
    }
  }, [handleCloseDialog]);

  // Handle overlay click to close
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseDialog();
    }
  };

  useEffect(() => {
    if (!verification) return;

    const msg = verification?.message;
    const status = verification?.status?.toLowerCase()?.trim();
    const comment = verification?.comment;

    if (status) {
      setDocuments((prev) =>
        prev.map((doc) => ({
          ...doc,
          status: verification?.status,
          ...(comment !== undefined && { comment }),
        })),
      );
    }

    if (!msg) return;

    let successMsg = "";
    let errorMsg = "";

    switch (status) {
      case "accepted":
        successMsg = msg;
        break;
      case "rejected":
      case "further_verification_requested":
      case "reupload_requested_clarity":
      case "manual_upload":
        errorMsg = msg;
        break;
      default:
        errorMsg = msg;
    }

    setMessage({ success: successMsg, error: errorMsg });
  }, [verification]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    if (showDialog) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showDialog]);

  return (
    <>
      <Toaster message={message} />
      <div
        className={`documents-header d-space-between ${isDarkMode ? "dark" : "light"}`}
      >
        <div className="documents-header-left">
          <div className="documents-icon">
            <img
              alt="documents-icon"
              src={
                isDarkMode
                  ? profileFilledDarkIcons.documentsFilledDark
                  : profilePurpleIcons.documentsFilledPurple
              }
            />
          </div>
          <div className="documents-header-text">
            <h1 className="documents-title-header">Documents</h1>
            <p className="subtitle">
              Upload your identity, residence, or other supporting documents.
            </p>
          </div>
        </div>

        {documentUploadEnabled && (
          <button className="upload-doc-btn" onClick={handleOpenDialog}>
            <Upload className="mb-1" />
            <span className="text-desktop"> Upload Document</span>
            <span className="text-mobile"> Upload</span>
          </button>
        )}
      </div>

      {/* UPLOAD DIALOG */}
      {showDialog && (
        <div
          className={`dialog-overlay ${
            isDarkMode ? "dialog-overlay-dark dark" : "light"
          }`}
          onClick={handleOverlayClick}
        >
          <div
            ref={dialogRef}
            className={`dialog-content ${
              isDarkMode ? "dialog-content-dark" : ""
            } ${isClosing ? "closing" : ""} ${isDragging ? "dragging" : ""}`}
          >
            {/* Drag Handle - Mobile Only */}
            <div
              className="dialog-drag-handle"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div
                className={`dialog-drag-indicator ${
                  isDarkMode ? "dialog-drag-indicator-dark" : ""
                }`}
              />
            </div>

            {/* Header */}
            <div className="dialog-header">
              <Upload
                className="sword-icon-color"
                style={{ width: "32px", height: "32px", marginRight: "16px" }}
              />
              <h2
                className={`dialog-title pl-3 ${
                  isDarkMode ? "dialog-title-dark" : ""
                }`}
              >
                Upload Document
              </h2>
              <button
                className={`dialog-close-btn ${
                  isDarkMode ? "dialog-close-btn-dark" : ""
                }`}
                onClick={handleCloseDialog}
              >
                <X size={20} />
              </button>
            </div>

            {/* Document Type Input */}
            <div className="dialog-form-group">
              <label
                className={`dialog-form-label ${
                  isDarkMode ? "dialog-form-label-dark" : ""
                }`}
              >
                Document Type
              </label>
              <select
                value={selectedDocumentType}
                onChange={handleDocumentTypeChange}
                className={`document-select text-medium-gray ${
                  isDarkMode ? "document-select-dark" : ""
                }`}
              >
                <option value="">Document Type</option>
                {furtherVerification ? (
                  <>
                    <option value="identity" disabled>
                      ID (Government issued)
                    </option>
                    <option value="bank_statement" disabled>
                      Bank Statement
                    </option>
                    <option value="utility" disabled>
                      Utility Bill
                    </option>
                    <option value="residence" disabled>
                      Residency / Lease Agreement
                    </option>
                    <option value="identity_with_code">
                      Identity with code
                    </option>
                  </>
                ) : (
                  <>
                    <option value="identity">ID (Government issued)</option>
                    <option value="bank_statement">Bank Statement</option>
                    <option value="utility">Utility Bill</option>
                    <option value="residence">
                      Residency / Lease Agreement
                    </option>
                  </>
                )}
              </select>
            </div>

            {/* Upload Section */}
            <div className="dialog-form-group">
              <label
                className={`dialog-form-label ${
                  isDarkMode ? "dialog-form-label-dark" : ""
                }`}
              >
                Upload Document
              </label>

              {!selectedFile ? (
                <div
                  className={`upload-dropzone ${
                    isDarkMode ? "upload-dropzone-dark" : ""
                  } ${isDragOver ? "drag-over" : ""}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={handleSelectFileClick}
                >
                  <div className="upload-icon-wrapper text-medium-gray d-start">
                    <Upload
                      className="icon-filled-primary-monochrome mx-lg-2 mx-md-2 mx-0"
                      size={24}
                    />
                    Upload Document
                  </div>
                  <input
                    type="file"
                    ref={inputRef}
                    capture="environment"
                    className="hidden-input"
                    onChange={handleFileChange}
                    accept="image/*,.pdf,.doc,.docx"
                  />
                </div>
              ) : (
                <div className="upload-dropzone d-center">
                  <div className="file-preview">
                    <span
                      className="file-cancel"
                      onClick={() => setSelectedFile(null)}
                    >
                      &times;
                    </span>
                    <p className="file-name mb-0">
                      <span style={{ fontWeight: 500 }}>
                        {selectedFile.name}
                      </span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="dialog-actions">
              <button
                className="deep-primary-monochrome-btn-dark"
                onClick={handleCloseDialog}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadFile}
                className="filled-medium-deep-yellow-btn-dark"
                disabled={!selectedFile || !selectedDocumentType}
              >
                Upload
              </button>
            </div>

            {/* Further Verification Note */}
            {furtherVerification &&
              selectedDocumentType === "identity_with_code" && (
                <p
                  style={{
                    marginTop: "20px",
                    fontSize: "13px",
                    color: "rgba(255, 255, 255, 0.5)",
                    lineHeight: "1.6",
                  }}
                >
                  <span style={{ color: "#ef4444" }}>
                    Your request has been declined by the admin. To resubmit
                    your identity verification, please take a clear photo of
                    yourself holding your valid ID (National ID Card, Passport,
                    Driving License, etc.) in one hand, and a sheet of paper
                    with this provided unique code{" "}
                    <span style={{ color: "#ffffff", fontWeight: "bold" }}>
                      {documentVerificationCode}
                    </span>{" "}
                    and{" "}
                    <span style={{ color: "#ffffff", fontWeight: "bold" }}>
                      Current Date
                    </span>{" "}
                    written on it in the other. Both your face, ID, and the code
                    must be clearly visible in the photo.
                  </span>
                </p>
              )}
            {isMobile && (
              <div className="swipe-handle-container-bottom-doc">
                <div className="swipe-handle-bottom" />
              </div>
            )}
          </div>
        </div>
      )}

      <div
        className={`document-upload-container ${isDarkMode ? "dark" : "light"}`}
      >
        <div
          className={`myaccount-table-container ${isDarkMode ? "dark" : ""}`}
        >
          <div className="myaccount-table-wrapper">
            <table className="profile-tables">
              <thead>
                <tr>
                  <th className="ps-4">Document Type</th>
                  <th>Uploaded On</th>
                  <th>Comment</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.length > 0 ? (
                  documents.map((doc, index) => (
                    <tr key={doc.id} className="withdrawal-row">
                      <td className="ps-4">{formatLabel(doc.type)}</td>
                      <td>{formatDateTime(doc.created_at)}</td>
                      <td>
                        {doc.comment ? (
                          <ReadMoreText text={doc.comment} limit={30} />
                        ) : (
                          "N/A"
                        )}
                      </td>

                      <td>{statusBadge(doc?.status)}</td>
                      <td>
                        <button
                          className="eye-button"
                          onClick={() => handleView(doc)}
                        >
                          <Eye size={20} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-data">
                      No documents found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <BackdropLoader show={loading} />
    </>
  );
};

export default MyAccount;
